/**
 * Mapper App — React Query hooks + API helpers.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isTokenValid } from '@/lib/auth';
import { cpFetch } from '@/lib/cpClient';
import { useAuthStore } from '@/store/authStore';
import { useSelectedAppId } from '@/hooks/useSelectedAppId';
import { useAppStore } from '@/store/appStore';
import { getAppSettings, updateAppSettings } from '@/features/apps/api';
import type {
  MapLayer,
  MapFeature,
  MapperStats,
  CreateLayerPayload,
  UpdateLayerPayload,
  CreateFeaturePayload,
  UpdateFeaturePayload,
  ImportPreview,
  CommitImportPayload,
  AgentMapPayload,
  LayerFilters,
  FeatureFilters,
  MapParcel,
  ParcelFilters,
  ParcelImportPreview,
  MapInfrastructure,
  InfrastructureFilters,
  CreateInfrastructurePayload,
  UpdateInfrastructurePayload,
  TwinState,
  ParcelTwinState,
  InfrastructureTaxonomy,
  LandCategory,
  MapperSharedParcelGroup,
  MapperBookmark,
  MapperSuggestionMetrics,
  WorkspaceApi,
  WorkspaceApiEndpoint,
  LinkedApiEndpoint,
  CreateGeeJobPayload,
  CreateGeeJobResult,
  MapperGeeJob,
  MapperWorkspaceSnapshot,
  MapLandUnit,
} from './types';

function hasStoredCpSession(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const raw = localStorage.getItem('dc.auth');
    if (!raw) return false;
    const auth = JSON.parse(raw);
    return typeof auth?.token === 'string' && isTokenValid(auth.token.trim());
  } catch {
    return false;
  }
}

function isMapperSessionReady(token: string | null | undefined): boolean {
  return (typeof token === 'string' && isTokenValid(token.trim())) || hasStoredCpSession();
}

/* ================================================================== */
/*  Taxonomy (infrastructure categories, templates — v2)                */
/* ================================================================== */

export function useInfrastructureTaxonomy(
  landCategory?: LandCategory | null,
  options: { enabled?: boolean } = {},
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  return useQuery<InfrastructureTaxonomy>({
    queryKey: ['mapper', 'taxonomy', appId, landCategory || null],
    queryFn: async () => {
      const qs = landCategory ? `?land_category=${encodeURIComponent(landCategory)}` : '';
      const res = await cpFetch(`/mapper/taxonomy${qs}`);
      return (res?.taxonomy ?? res) as InfrastructureTaxonomy;
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 5 * 60 * 1000, // 5 minutes — matches backend cache
    gcTime: 10 * 60 * 1000,
  });
}

/* ================================================================== */
/*  Stats                                                               */
/* ================================================================== */

export function useMapperStats(enabled = true) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<MapperStats>({
    queryKey: ['mapper', 'stats', appId],
    queryFn: async () => {
      const res = await cpFetch('/mapper/stats');
      return res?.stats ?? res;
    },
    enabled: sessionReady && !!appId && enabled,
    staleTime: 15_000,
  });
}

export function useMapperWorkspaceSettings() {
  const { token } = useAuthStore();
  const { appId } = useAppStore();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<MapperWorkspaceSnapshot | null>({
    queryKey: ['mapper', 'workspace-settings', appId],
    queryFn: async () => {
      if (!appId) return null;
      const res = await getAppSettings(appId);
      return (res?.settings?.mapper?.workspace ?? null) as MapperWorkspaceSnapshot | null;
    },
    enabled: sessionReady && !!appId,
    staleTime: 15_000,
  });
}

export function useSaveMapperWorkspace() {
  const qc = useQueryClient();
  const { appId } = useAppStore();
  return useMutation({
    mutationFn: async (snapshot: MapperWorkspaceSnapshot) => {
      if (!appId) {
        throw new Error('Select an app before saving mapper progress.');
      }
      const savedAt = new Date().toISOString();
      await updateAppSettings(appId, {
        mapper: {
          workspace: {
            ...snapshot,
            saved_at: savedAt,
          },
        },
      });
      return {
        ...snapshot,
        saved_at: savedAt,
      } as MapperWorkspaceSnapshot;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'workspace-settings', appId] });
    },
  });
}

/* ================================================================== */
/*  Layer hooks                                                         */
/* ================================================================== */

export function useMapLayers(filters: LayerFilters = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery({
    queryKey: ['mapper', 'layers', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.layer_type) params.set('layer_type', filters.layer_type);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const res = await cpFetch(`/mapper/layers${qs ? `?${qs}` : ''}`);
      return {
        layers: (res?.layers ?? []) as MapLayer[],
        total: res?.total ?? 0,
      };
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 10_000,
  });
}

export function useCreateLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateLayerPayload) => {
      const res = await cpFetch('/mapper/layers', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.layer ?? res) as MapLayer;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'layers'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'stats'] });
    },
  });
}

export function useUpdateLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateLayerPayload & { id: string }) => {
      const res = await cpFetch(`/mapper/layers/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.layer ?? res) as MapLayer;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'layers'] });
    },
  });
}

export function useDeleteLayer() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await cpFetch(`/mapper/layers/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

/* ================================================================== */
/*  Feature hooks                                                       */
/* ================================================================== */

export function useMapFeatures(filters: FeatureFilters = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery({
    queryKey: ['mapper', 'features', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.layer_id) params.set('layer_id', filters.layer_id);
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.linked_asset_id) params.set('linked_asset_id', filters.linked_asset_id);
      if (filters.status) params.set('status', filters.status);
      if (filters.bbox) params.set('bbox', filters.bbox);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const res = await cpFetch(`/mapper/features${qs ? `?${qs}` : ''}`);
      return {
        features: (res?.features ?? []) as MapFeature[],
        total: res?.total ?? 0,
      };
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 5_000,
  });
}

export function useLayerFeatures(layerId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['mapper', 'layer-features', appId, layerId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/layers/${layerId}/features`);
      return (res?.features ?? []) as MapFeature[];
    },
    enabled: sessionReady && !!appId && !!layerId,
    staleTime: 5_000,
  });
}

export function useCreateFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateFeaturePayload) => {
      const res = await cpFetch('/mapper/features', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.feature ?? res) as MapFeature;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

export function useUpdateFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateFeaturePayload & { id: string }) => {
      const res = await cpFetch(`/mapper/features/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.feature ?? res) as MapFeature;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

export function useDeleteFeature() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await cpFetch(`/mapper/features/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

/* ================================================================== */
/*  Import hooks                                                        */
/* ================================================================== */

export function useImportFile() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await cpFetch('/mapper/import', {
        method: 'POST',
        body: formData,
        headers: {}, // Let browser set multipart Content-Type
      });
      return res as ImportPreview;
    },
  });
}

export function useCommitImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CommitImportPayload) => {
      const res = await cpFetch('/mapper/import/commit', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res as { success: boolean; layer_id: string; imported: number; feature_ids: string[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

/* ================================================================== */
/*  Linking hooks                                                       */
/* ================================================================== */

export function useLinkLayerEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ layerId, entity_id }: { layerId: string; entity_id: string }) => {
      const res = await cpFetch(`/mapper/layers/${layerId}/link-entity`, {
        method: 'POST',
        body: JSON.stringify({ entity_id }),
      });
      return (res?.layer ?? res) as MapLayer;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'layers'] });
    },
  });
}

export function useLinkFeatureAsset() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ featureId, asset_id }: { featureId: string; asset_id: string }) => {
      const res = await cpFetch(`/mapper/features/${featureId}/link-asset`, {
        method: 'POST',
        body: JSON.stringify({ asset_id }),
      });
      return (res?.feature ?? res) as MapFeature;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

/* ================================================================== */
/*  Agentic mapping                                                     */
/* ================================================================== */

export function useAgentMap() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: AgentMapPayload) => {
      const res = await cpFetch('/mapper/agent-map', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res as { success: boolean; pipeline_id: string; job_id?: string; message: string };
    },
    onSuccess: () => {
      // Delay refetch to let background detection complete.
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['mapper'] });
      }, 5000);
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['mapper'] });
      }, 15000);
    },
  });
}

export function useCreateGeeJob() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateGeeJobPayload) => {
      const res = await cpFetch('/mapper/gee/jobs', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res as CreateGeeJobResult;
    },
    onSuccess: (_result, payload) => {
      if (payload.job_type !== 'ndvi_classification') {
        qc.invalidateQueries({ queryKey: ['mapper'] });
      }
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['mapper'] });
      }, 5000);
      setTimeout(() => {
        qc.invalidateQueries({ queryKey: ['mapper'] });
      }, 15000);
    },
  });
}

export function useGeeJob(
  jobId: string | undefined | null,
  options: { enabled?: boolean; pollWhilePending?: boolean } = {}
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const enabled = options.enabled !== undefined ? options.enabled : true;
  const pollWhilePending = options.pollWhilePending !== undefined ? options.pollWhilePending : false;

  return useQuery<MapperGeeJob>({
    queryKey: ['mapper', 'gee-job', appId, jobId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/gee/jobs/${jobId}`);
      return (res?.job ?? res) as MapperGeeJob;
    },
    enabled: sessionReady && !!appId && !!jobId && enabled,
    staleTime: 2_000,
    refetchInterval: pollWhilePending ? 4_000 : false,
  });
}

/* ================================================================== */
/*  Parcel hooks                                                       */
/* ================================================================== */

type ParcelQueryResult = {
  parcels: MapParcel[];
  total: number | null;
};

function buildParcelSearchParams(filters: ParcelFilters): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
  if (filters.province) params.set('province', filters.province);
  if (filters.province_code) params.set('province_code', filters.province_code);
  if (filters.sg_number) params.set('sg_number', filters.sg_number);
  if (filters.prcl_key) params.set('prcl_key', filters.prcl_key);
  if (filters.external_id) params.set('external_id', filters.external_id);
  if (filters.country_code) params.set('country_code', filters.country_code);
  if (filters.admin_level_1) params.set('admin_level_1', filters.admin_level_1);
  if (filters.admin_level_2) params.set('admin_level_2', filters.admin_level_2);
  if (filters.registry_type) params.set('registry_type', filters.registry_type);
  if (filters.source_adapter) params.set('source_adapter', filters.source_adapter);
  if (filters.source_pack_id) params.set('source_pack_id', filters.source_pack_id);
  if (filters.natural_key) params.set('natural_key', filters.natural_key);
  if (filters.natural_key_value) params.set('natural_key_value', filters.natural_key_value);
  if (filters.identifier_key) params.set('identifier_key', filters.identifier_key);
  if (filters.identifier_value) params.set('identifier_value', filters.identifier_value);
  if (filters.search) params.set('search', filters.search);
  if (filters.search_scope) params.set('search_scope', filters.search_scope);
  if (filters.erf_number) params.set('erf_number', filters.erf_number);
  if (filters.land_category) params.set('land_category', filters.land_category);
  if (filters.land_type) params.set('land_type', filters.land_type);
  if (filters.bbox) params.set('bbox', filters.bbox);
  if (filters.fields) params.set('fields', filters.fields);
  if (filters.page) params.set('page', String(filters.page));
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.skip_total) params.set('skip_total', '1');
  return params;
}

async function fetchMapParcels(filters: ParcelFilters, signal?: AbortSignal): Promise<ParcelQueryResult> {
  const qs = buildParcelSearchParams(filters).toString();
  const res = await cpFetch(`/mapper/parcels${qs ? `?${qs}` : ''}`, { signal });
  return {
    parcels: (res?.parcels ?? []) as MapParcel[],
    total: typeof res?.total === 'number' ? res.total : null,
  };
}

export async function fetchMapParcelById(parcelId: string, signal?: AbortSignal): Promise<MapParcel> {
  const res = await cpFetch(`/mapper/parcels/${parcelId}`, { signal });
  return (res?.parcel ?? res) as MapParcel;
}

export async function fetchParcelOverlayGeoJSON(
  bbox: string,
  signal?: AbortSignal,
): Promise<GeoJSON.FeatureCollection> {
  const res = await cpFetch(`/mapper/parcels/overlay?bbox=${encodeURIComponent(bbox)}`, { signal });
  if (res?.type === 'FeatureCollection' && Array.isArray(res.features)) {
    return res as GeoJSON.FeatureCollection;
  }
  return { type: 'FeatureCollection', features: [] };
}

async function fetchMapParcelsByIds(parcelIds: string[], signal?: AbortSignal): Promise<MapParcel[]> {
  if (parcelIds.length === 0) return [];
  const res = await cpFetch('/mapper/parcels/batch', {
    method: 'POST',
    body: JSON.stringify({ ids: parcelIds }),
    signal,
  });
  const parcels = (res?.parcels ?? []) as MapParcel[];
  if (parcels.length <= 1) return parcels;
  const parcelsById = new Map(parcels.map((parcel) => [String(parcel?.id || '').trim(), parcel]));
  return parcelIds
    .map((parcelId) => parcelsById.get(parcelId))
    .filter((parcel): parcel is MapParcel => Boolean(parcel));
}

function parseViewportBbox(bbox?: string): [number, number, number, number] | null {
  if (!bbox) return null;
  const parts = String(bbox).split(',').map(Number);
  if (parts.length !== 4 || !parts.every(Number.isFinite)) return null;
  const [west, south, east, north] = parts;
  if (west >= east || south >= north) return null;
  return [west, south, east, north];
}

function formatViewportBbox(bounds: [number, number, number, number]): string {
  return bounds.join(',');
}

function splitViewportBbox(
  bounds: [number, number, number, number],
  segments: number,
): string[] {
  if (segments <= 1) return [formatViewportBbox(bounds)];
  const [west, south, east, north] = bounds;
  const stepX = (east - west) / segments;
  const stepY = (north - south) / segments;
  const boxes: string[] = [];

  for (let row = 0; row < segments; row += 1) {
    const tileSouth = south + row * stepY;
    const tileNorth = row === segments - 1 ? north : tileSouth + stepY;
    for (let col = 0; col < segments; col += 1) {
      const tileWest = west + col * stepX;
      const tileEast = col === segments - 1 ? east : tileWest + stepX;
      boxes.push(formatViewportBbox([tileWest, tileSouth, tileEast, tileNorth]));
    }
  }

  return boxes;
}

function getViewportParcelTileGrid(zoom?: number): number {
  if (!Number.isFinite(zoom)) return 1;
  if ((zoom ?? 0) < 13) return 2;
  return 1;
}

async function mapWithConcurrency<T, R>(
  items: T[],
  concurrency: number,
  mapper: (item: T) => Promise<R>,
): Promise<R[]> {
  if (items.length === 0) return [];
  const results = new Array<R>(items.length);
  let nextIndex = 0;

  async function worker() {
    while (nextIndex < items.length) {
      const currentIndex = nextIndex;
      nextIndex += 1;
      results[currentIndex] = await mapper(items[currentIndex]);
    }
  }

  const workerCount = Math.max(1, Math.min(concurrency, items.length));
  await Promise.all(Array.from({ length: workerCount }, () => worker()));
  return results;
}

export function useMapParcels(filters: ParcelFilters = {}) {
  const { token } = useAuthStore();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery({
    queryKey: ['mapper', 'parcels', queryFilters],
    queryFn: async ({ signal }) => fetchMapParcels(filters, signal),
    enabled: sessionReady && explicitEnabled,
    staleTime: 15_000,
  });
}

export function useViewportMapParcels(
  filters: ParcelFilters = {},
  options: { zoom?: number } = {},
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const tileGrid = filters.bbox ? getViewportParcelTileGrid(options.zoom) : 1;
  const queryFilters = {
    ...filters,
    viewport_tile_grid: tileGrid,
    viewport_zoom: Number.isFinite(options.zoom) ? Number((options.zoom ?? 0).toFixed(1)) : null,
  };
  delete queryFilters.enabled;

  return useQuery({
    queryKey: ['mapper', 'parcels', 'viewport', appId, queryFilters],
    queryFn: async ({ signal }) => {
      const bounds = parseViewportBbox(filters.bbox);
      if (!bounds || tileGrid <= 1) {
        return fetchMapParcels(filters, signal);
      }

      const tileFilters = splitViewportBbox(bounds, tileGrid).map((bbox) => ({
        ...filters,
        bbox,
      }));
      const tileResults = await mapWithConcurrency(tileFilters, 4, (tileFilter) => fetchMapParcels(tileFilter, signal));
      const merged: MapParcel[] = [];
      const seen = new Set<string>();

      for (const result of tileResults) {
        for (const parcel of result.parcels) {
          const parcelId = String(parcel?.id || '').trim();
          if (!parcelId || seen.has(parcelId)) continue;
          seen.add(parcelId);
          merged.push(parcel);
        }
      }

      return { parcels: merged, total: null };
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 15_000,
  });
}

export function useMapParcel(parcelId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['mapper', 'parcels', 'detail', appId, parcelId],
    queryFn: async ({ signal }) => fetchMapParcelById(String(parcelId || ''), signal),
    enabled: sessionReady && !!appId && !!parcelId,
    staleTime: 15_000,
  });
}

export function useLookupParcelByPoint() {
  return useMutation({
    mutationFn: async ({ lng, lat }: { lng: number; lat: number }) => {
      const params = new URLSearchParams({
        lng: String(lng),
        lat: String(lat),
      });
      const res = await cpFetch(`/mapper/parcels/by-point?${params.toString()}`);
      return (res?.parcel ?? null) as MapParcel | null;
    },
  });
}

export function useImportParcels() {
  return useMutation({
    mutationFn: async (file: File) => {
      const formData = new FormData();
      formData.append('file', file);
      const res = await cpFetch('/mapper/parcels/import', {
        method: 'POST',
        body: formData,
        headers: {},
      });
      return res as ParcelImportPreview;
    },
  });
}

export function useCommitParcelImport() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { import_id: string; selected_indices: number[]; linked_entity_id?: string }) => {
      const res = await cpFetch('/mapper/parcels/import/commit', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return res as { success: boolean; imported: number; parcel_ids: string[] };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcels'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'stats'] });
    },
  });
}

export function useAssignParcelEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ parcelId, entity_id }: { parcelId: string; entity_id: string }) => {
      const res = await cpFetch(`/mapper/parcels/${parcelId}/assign-entity`, {
        method: 'POST',
        body: JSON.stringify({ entity_id }),
      });
      return res as { success: boolean; parcel: MapParcel; infrastructure_updated: number };
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcels'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
    },
  });
}

export function useCreateParcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: {
      feature_name?: string | null;
      farm_name?: string | null;
      external_id?: string | null;
      sg_number?: string | null;
      erf_number?: string | null;
      local_identifiers?: Record<string, string | null>;
      geometry: GeoJSON.Geometry;
      linked_entity_id?: string | null;
      land_category?: LandCategory | null;
      land_type?: string | null;
      country_code?: string | null;
      admin_level_1?: string | null;
      admin_level_2?: string | null;
    }) => {
      const res = await cpFetch('/mapper/parcels', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.parcel ?? res) as MapParcel;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcels'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'stats'] });
    },
  });
}

export function useUpdateParcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: {
      id: string;
      feature_name?: string | null;
      farm_name?: string | null;
      external_id?: string | null;
      local_identifiers?: Record<string, string | null>;
      linked_entity_id?: string | null;
      active_template_ids?: string[];
      land_category?: LandCategory | null;
      land_type?: string | null;
      erf_number?: string | null;
      country_code?: string | null;
      admin_level_1?: string | null;
      admin_level_2?: string | null;
      natural_key_field?: string | null;
      natural_key_value?: string | null;
    }) => {
      const res = await cpFetch(`/mapper/parcels/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.parcel ?? res) as MapParcel;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcels'] });
    },
  });
}

export function useSyncParcelToPim() {
  return useMutation({
    mutationFn: async (parcelId: string) => {
      const res = await cpFetch(`/mapper/parcels/${parcelId}/sync-farm`, {
        method: 'POST',
      });
      return res as { success: boolean };
    },
  });
}

export function useDeleteParcel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (parcelId: string) => {
      await cpFetch(`/mapper/parcels/${parcelId}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcels'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'stats'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
    },
  });
}

/* ================================================================== */
/*  Infrastructure hooks                                                */
/* ================================================================== */

export function useMapInfrastructure(filters: InfrastructureFilters = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery({
    queryKey: ['mapper', 'infrastructure', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.parcel_id) params.set('parcel_id', filters.parcel_id);
      if (filters.infra_type) params.set('infra_type', filters.infra_type);
      if (filters.category) params.set('category', filters.category);
      if (filters.subcategory) params.set('subcategory', filters.subcategory);
      if (filters.layer_key) params.set('layer_key', filters.layer_key);
      if (filters.land_category) params.set('land_category', filters.land_category);
      if (filters.job_id) params.set('job_id', filters.job_id);
      if (filters.suggestion_source_job_id) params.set('suggestion_source_job_id', filters.suggestion_source_job_id);
      if (filters.industry) params.set('industry', filters.industry);
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.linked_device_id) params.set('linked_device_id', filters.linked_device_id);
      if (filters.status) params.set('status', filters.status);
      if (filters.bbox) params.set('bbox', filters.bbox);
      if (filters.page) params.set('page', String(filters.page));
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const res = await cpFetch(`/mapper/infrastructure${qs ? `?${qs}` : ''}`);
      return {
        items: (res?.infrastructure ?? res?.items ?? []) as MapInfrastructure[],
        total: res?.total ?? 0,
      };
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 5_000,
  });
}

export function useInfrastructure(infraId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<MapInfrastructure>({
    queryKey: ['mapper', 'infrastructure', 'detail', appId, infraId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/infrastructure/${infraId}`);
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    enabled: sessionReady && !!appId && !!infraId,
    staleTime: 5_000,
  });
}

export function useCreateInfrastructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: CreateInfrastructurePayload) => {
      const res = await cpFetch('/mapper/infrastructure', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

export function useUpdateInfrastructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: UpdateInfrastructurePayload & { id: string }) => {
      const res = await cpFetch(`/mapper/infrastructure/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

export function useDeleteInfrastructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await cpFetch(`/mapper/infrastructure/${id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper'] });
    },
  });
}

export function useLinkInfraDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ infraId, device_id }: { infraId: string; device_id: string }) => {
      const res = await cpFetch(`/mapper/infrastructure/${infraId}/link-device`, {
        method: 'POST',
        body: JSON.stringify({ device_id }),
      });
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useUnlinkInfraDevice() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ infraId, device_id }: { infraId: string; device_id: string }) => {
      await cpFetch(`/mapper/infrastructure/${infraId}/link-device/${device_id}`, { method: 'DELETE' });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useLinkInfrastructureEntity() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ infraId, entity_id }: { infraId: string; entity_id: string }) => {
      const res = await cpFetch(`/mapper/infrastructure/${infraId}/link-entity`, {
        method: 'POST',
        body: JSON.stringify({ entity_id }),
      });
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useSyncInfrastructure() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (infraId: string) => {
      return cpFetch(`/mapper/infrastructure/${infraId}/sync`, {
        method: 'POST',
      }) as Promise<{ success: boolean; infrastructure_id: string; template_id?: string; product_id?: string; asset_id?: string; message?: string }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useLinkInfrastructureApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ infraId, link }: { infraId: string; link: LinkedApiEndpoint }) => {
      const res = await cpFetch(`/mapper/infrastructure/${infraId}/link-api`, {
        method: 'POST',
        body: JSON.stringify(link),
      });
      return (res?.infrastructure ?? res?.item ?? res) as MapInfrastructure;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useUnlinkInfrastructureApi() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ infraId, api_id, endpoint_id }: { infraId: string; api_id: string; endpoint_id: string }) => {
      await cpFetch(`/mapper/infrastructure/${infraId}/link-api/${encodeURIComponent(api_id)}/${encodeURIComponent(endpoint_id)}`, {
        method: 'DELETE',
      });
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'twin-state'] });
    },
  });
}

export function useInfraTwinState(infraId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<TwinState>({
    queryKey: ['mapper', 'twin-state', appId, infraId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/infrastructure/${infraId}/twin-state`);
      return (res?.twin ?? res) as TwinState;
    },
    enabled: sessionReady && !!appId && !!infraId,
    staleTime: 10_000,
  });
}

export function useParcelTwinState(parcelId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<ParcelTwinState>({
    queryKey: ['mapper', 'parcel-twin-state', appId, parcelId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/parcels/${parcelId}/twin-state`);
      return (res?.twin ?? res) as ParcelTwinState;
    },
    enabled: sessionReady && !!appId && !!parcelId,
    staleTime: 10_000,
  });
}

export function useInfraWithinParcel(parcelId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['mapper', 'infrastructure', 'within', appId, parcelId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/infrastructure/within/${parcelId}`);
      return (res?.infrastructure ?? res?.items ?? []) as MapInfrastructure[];
    },
    enabled: sessionReady && !!appId && !!parcelId,
    staleTime: 10_000,
  });
}

export function useInfraWithinParcels(parcelIds: string[], options: { enabled?: boolean } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  const normalizedIds = Array.from(new Set(
    (parcelIds || [])
      .map((parcelId) => String(parcelId || '').trim())
      .filter(Boolean)
  ));

  return useQuery<MapInfrastructure[]>({
    queryKey: ['mapper', 'infrastructure', 'within-many', appId, normalizedIds],
    queryFn: async () => {
      const results = await Promise.all(normalizedIds.map(async (parcelId) => {
        const res = await cpFetch(`/mapper/infrastructure/within/${parcelId}`);
        return (res?.infrastructure ?? res?.items ?? []) as MapInfrastructure[];
      }));
      const deduped = new Map<string, MapInfrastructure>();
      for (const items of results) {
        for (const item of items) {
          if (item?.id) deduped.set(item.id, item);
        }
      }
      return Array.from(deduped.values());
    },
    enabled: sessionReady && !!appId && explicitEnabled && normalizedIds.length > 0,
    staleTime: 10_000,
  });
}

export function useMapperParcelGroups(linkedEntityId?: string | null, options: { enabled?: boolean } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  return useQuery<MapperSharedParcelGroup[]>({
    queryKey: ['mapper', 'parcel-groups', appId, linkedEntityId || 'all'],
    queryFn: async () => {
      const qs = linkedEntityId ? `?linked_entity_id=${encodeURIComponent(linkedEntityId)}` : '';
      const res = await cpFetch(`/mapper/parcel-groups${qs}`);
      return (res?.groups ?? []) as MapperSharedParcelGroup[];
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 15_000,
  });
}

export function useMapParcelsByIds(parcelIds: string[], options: { enabled?: boolean } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  const normalizedIds = Array.from(new Set(
    (parcelIds || [])
      .map((parcelId) => String(parcelId || '').trim())
      .filter(Boolean)
  ));

  return useQuery<MapParcel[]>({
    queryKey: ['mapper', 'parcels', 'by-ids', appId, normalizedIds],
    queryFn: async ({ signal }) => fetchMapParcelsByIds(normalizedIds, signal),
    enabled: sessionReady && !!appId && explicitEnabled && normalizedIds.length > 0,
    staleTime: 15_000,
  });
}

export function useCreateMapperParcelGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Pick<MapperSharedParcelGroup, 'name'> & Partial<MapperSharedParcelGroup>) => {
      const res = await cpFetch('/mapper/parcel-groups', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.group ?? res) as MapperSharedParcelGroup;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'parcel-groups'] }),
  });
}

export function useUpdateMapperParcelGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...payload }: Partial<MapperSharedParcelGroup> & { id: string }) => {
      const res = await cpFetch(`/mapper/parcel-groups/${id}`, {
        method: 'PUT',
        body: JSON.stringify(payload),
      });
      return (res?.group ?? res) as MapperSharedParcelGroup;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'parcel-groups'] }),
  });
}

export function useDeleteMapperParcelGroup() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => cpFetch(`/mapper/parcel-groups/${id}`, { method: 'DELETE' }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcel-groups'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'bookmarks'] });
    },
  });
}

export function useMapperBookmarks(
  filters: { linked_entity_id?: string; parcel_id?: string; group_id?: string; enabled?: boolean } = {},
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const hasFilters = Boolean(filters.linked_entity_id || filters.parcel_id || filters.group_id);
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery<MapperBookmark[]>({
    queryKey: ['mapper', 'bookmarks', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.parcel_id) params.set('parcel_id', filters.parcel_id);
      if (filters.group_id) params.set('group_id', filters.group_id);
      const qs = params.toString();
      const res = await cpFetch(`/mapper/bookmarks${qs ? `?${qs}` : ''}`);
      return (res?.bookmarks ?? []) as MapperBookmark[];
    },
    enabled: sessionReady && !!appId && explicitEnabled && hasFilters,
    staleTime: 15_000,
  });
}

export function useCreateMapperBookmark() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: Pick<MapperBookmark, 'name'> & Partial<MapperBookmark>) => {
      const res = await cpFetch('/mapper/bookmarks', {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return (res?.bookmark ?? res) as MapperBookmark;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'bookmarks'] }),
  });
}

export function useImportLegacyMapperWorkspace() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (snapshot: MapperWorkspaceSnapshot) => {
      return cpFetch('/mapper/workspace/import-legacy', {
        method: 'POST',
        body: JSON.stringify({ snapshot }),
      }) as Promise<{ success: boolean; imported_groups: number; imported_bookmarks: number }>;
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'parcel-groups'] });
      qc.invalidateQueries({ queryKey: ['mapper', 'bookmarks'] });
    },
  });
}

export function useMapperSuggestions(
  filters: { parcel_id?: string; linked_entity_id?: string; job_id?: string; status?: string; enabled?: boolean } = {},
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const hasScope = Boolean(filters.parcel_id || filters.linked_entity_id || filters.job_id);
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery<MapInfrastructure[]>({
    queryKey: ['mapper', 'suggestions', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.parcel_id) params.set('parcel_id', filters.parcel_id);
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.job_id) params.set('job_id', filters.job_id);
      if (filters.status) params.set('status', filters.status);
      const qs = params.toString();
      const res = await cpFetch(`/mapper/suggestions${qs ? `?${qs}` : ''}`);
      return (res?.suggestions ?? []) as MapInfrastructure[];
    },
    enabled: sessionReady && !!appId && explicitEnabled && hasScope,
    staleTime: 5_000,
  });
}

export function useReviewSuggestions() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ ids, status }: { ids: string[]; status: 'active' | 'rejected' | 'review' }) => {
      const res = await cpFetch('/mapper/suggestions/review', {
        method: 'POST',
        body: JSON.stringify({ ids, status }),
      });
      return (res?.suggestions ?? []) as MapInfrastructure[];
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper'] }),
  });
}

export function useSuggestionMetrics(
  filters: { parcel_id?: string; linked_entity_id?: string; job_id?: string; enabled?: boolean } = {},
) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = filters.enabled !== undefined ? filters.enabled : true;
  const hasScope = Boolean(filters.parcel_id || filters.linked_entity_id || filters.job_id);
  const queryFilters = { ...filters };
  delete queryFilters.enabled;
  return useQuery<MapperSuggestionMetrics>({
    queryKey: ['mapper', 'suggestion-metrics', appId, queryFilters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.parcel_id) params.set('parcel_id', filters.parcel_id);
      if (filters.linked_entity_id) params.set('linked_entity_id', filters.linked_entity_id);
      if (filters.job_id) params.set('job_id', filters.job_id);
      const qs = params.toString();
      const res = await cpFetch(`/mapper/suggestions/metrics${qs ? `?${qs}` : ''}`);
      return (res?.metrics ?? {}) as MapperSuggestionMetrics;
    },
    enabled: sessionReady && !!appId && explicitEnabled && hasScope,
    staleTime: 5_000,
  });
}

export function useMapperScanJob(jobId: string | undefined | null) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['mapper', 'scan-job', appId, jobId],
    queryFn: async () => {
      const res = await cpFetch(`/mapper/scan-jobs/${jobId}/status`);
      return res?.job ?? res;
    },
    enabled: sessionReady && !!appId && !!jobId,
    staleTime: 3_000,
    refetchInterval: 4_000,
  });
}

export function useWorkspaceApis(filters: { search?: string; status?: string; group?: string; limit?: number } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<WorkspaceApi[]>({
    queryKey: ['mapper', 'workspace-apis', appId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.search) params.set('search', filters.search);
      if (filters.status) params.set('status', filters.status);
      if (filters.group) params.set('group', filters.group);
      if (filters.limit) params.set('limit', String(filters.limit));
      if (appId) params.set('app_id', appId);
      const qs = params.toString();
      const res = await cpFetch(`/api-ingest/workspace/apis${qs ? `?${qs}` : ''}`);
      return (res?.apis ?? res?.items ?? []) as WorkspaceApi[];
    },
    enabled: sessionReady && !!appId,
    staleTime: 60_000,
  });
}

export function useApiEndpoints(apiId: string | undefined) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery<WorkspaceApiEndpoint[]>({
    queryKey: ['mapper', 'api-endpoints', appId, apiId],
    queryFn: async () => {
      const res = await cpFetch(`/api-ingest/workspace/apis/${encodeURIComponent(apiId || '')}/detail?history_limit=1`);
      return (res?.detail?.endpoints ?? res?.item?.endpoints ?? res?.endpoints ?? []) as WorkspaceApiEndpoint[];
    },
    enabled: sessionReady && !!appId && !!apiId,
    staleTime: 60_000,
  });
}

/* ================================================================== */
/*  IoT device listing (from IoT Shack, for device palette)            */
/* ================================================================== */

export function useIoTDevices(filters: { q?: string; type?: string; limit?: number } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['iot', 'devices', appId, filters],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (filters.q) params.set('q', filters.q);
      if (filters.type) params.set('type', filters.type);
      if (filters.limit) params.set('limit', String(filters.limit));
      const qs = params.toString();
      const res = await cpFetch(`/iot/devices${qs ? `?${qs}` : ''}`);
      return (res?.items ?? res?.devices ?? []) as Array<{
        deviceId: string;
        type: string;
        status: string;
        manufacturer?: string;
        model?: string;
        metadata?: Record<string, unknown>;
      }>;
    },
    enabled: sessionReady && !!appId,
    staleTime: 15_000,
  });
}

/* ================================================================== */
/*  Land units (L2 — map_land_units)                                   */
/* ================================================================== */

export function useMapLandUnits(options: { enabled?: boolean; tag?: string } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  return useQuery<{ land_units: MapLandUnit[] }>({
    queryKey: ['mapper', 'land-units', appId, options.tag || null],
    queryFn: async () => {
      const qs = options.tag ? `?tag=${encodeURIComponent(options.tag)}` : '';
      const res = await cpFetch(`/mapper/land-units${qs}`);
      if (!res.ok) throw new Error('Failed to load land units');
      return res.json();
    },
    enabled: sessionReady && !!appId && explicitEnabled,
  });
}

export function useCreateLandUnit() {
  const qc = useQueryClient();
  const appId = useSelectedAppId();
  return useMutation({
    mutationFn: async (body: Partial<MapLandUnit>) => {
      const res = await cpFetch('/mapper/land-units', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json())?.error || 'Create failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'land-units', appId] }),
  });
}

export function useUpdateLandUnit() {
  const qc = useQueryClient();
  const appId = useSelectedAppId();
  return useMutation({
    mutationFn: async ({ id, ...body }: Partial<MapLandUnit> & { id: string }) => {
      const res = await cpFetch(`/mapper/land-units/${encodeURIComponent(id)}`, {
        method: 'PUT',
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error((await res.json())?.error || 'Update failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'land-units', appId] }),
  });
}

export function useDeleteLandUnit() {
  const qc = useQueryClient();
  const appId = useSelectedAppId();
  return useMutation({
    mutationFn: async (id: string) => {
      const res = await cpFetch(`/mapper/land-units/${encodeURIComponent(id)}`, { method: 'DELETE' });
      if (!res.ok) throw new Error((await res.json())?.error || 'Delete failed');
      return res.json();
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['mapper', 'land-units', appId] }),
  });
}

export function useRegistryTemplates(templateIds?: string[]) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  return useQuery({
    queryKey: ['mapper', 'registry-templates', appId, templateIds?.join(',') || 'all'],
    queryFn: async () => {
      const qs = templateIds?.length
        ? `?template_ids=${encodeURIComponent(templateIds.join(','))}`
        : '?iku_prefix=iku:infrastructure';
      const res = await cpFetch(`/mapper/registry/templates${qs}`);
      if (!res.ok) throw new Error('Failed to load registry templates');
      return res.json();
    },
    enabled: sessionReady && !!appId,
    staleTime: 120_000,
  });
}

export function useMapperSuggestionsReview() {
  const qc = useQueryClient();
  const appId = useSelectedAppId();
  return useMutation({
    mutationFn: async (body: { ids: string[]; status: 'active' | 'rejected' | 'review' }) => {
      const res = await cpFetch('/mapper/suggestions/review', { method: 'POST', body: JSON.stringify(body) });
      if (!res.ok) throw new Error((await res.json())?.error || 'Review failed');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['mapper', 'suggestions', appId] });
      qc.invalidateQueries({ queryKey: ['mapper', 'infrastructure', appId] });
    },
  });
}

export interface MapperDeedsStatus {
  enabled: boolean;
  configured?: boolean;
  provider?: string;
  via?: string;
  api_id?: string;
  reason?: string;
  message?: string;
}

/** Deeds lookup status — always safe to call; never triggers an external call. */
export function useMapperDeedsStatus(options: { enabled?: boolean } = {}) {
  const { token } = useAuthStore();
  const appId = useSelectedAppId();
  const sessionReady = isMapperSessionReady(token);
  const explicitEnabled = options.enabled !== undefined ? options.enabled : true;
  return useQuery<MapperDeedsStatus>({
    queryKey: ['mapper', 'deeds-status', appId],
    queryFn: async () => {
      const res = await cpFetch('/mapper/deeds/status');
      return {
        enabled: Boolean(res?.enabled),
        configured: Boolean(res?.configured),
        provider: res?.provider,
        via: res?.via,
        api_id: res?.api_id,
        reason: res?.reason,
        message: res?.message,
      } as MapperDeedsStatus;
    },
    enabled: sessionReady && !!appId && explicitEnabled,
    staleTime: 60_000,
  });
}

export interface DeedsOwner {
  type: 'trust' | 'company' | 'person' | string;
  name: string | null;
  share_perc: number | null;
  registration_number?: string | null;
  id_masked?: string | null;
  entity_key: string | null;
  portfolio_status: 'complete' | 'vendor_endpoint_pending' | 'skipped_lookup_cap' | 'unsupported_owner' | string | null;
}

export interface DeedsPortfolioProperty {
  lpicode: string;
  deeds_office?: string | null;
  property_type?: string | null;
  erf?: number | null;
  portion?: number | null;
  extent_ha?: number | null;
  title_deed?: string | null;
  registration_date?: string | null;
  owner_keys: string[];
  sources: string[];
  parcel: { id: string; farm_name: string | null; area_ha: number | null; linked_entity_id: string | null } | null;
}

export interface DeedsPortfolioResult {
  success: boolean;
  status?: 'ok' | 'deeds_not_found';
  error?: string;
  message?: string;
  details?: { hint?: string; [key: string]: unknown };
  anchor?: { sg_code: string; sg_source: string | null; parcel: { id: string; farm_name: string | null } | null };
  owners?: DeedsOwner[];
  portfolio?: { status: string; matched?: number; unmatched?: number; properties: DeedsPortfolioProperty[] };
  billing?: { billable_calls: number; cached_calls: number; cost_per_call: number | null; currency: string | null; estimated_cost: number | null };
  link?: { entity_id: string; linked: Array<{ parcel_id: string; error: string | null }>; skipped: Array<{ parcel_id: string; reason: string }> } | null;
}

export interface DeedsPortfolioRequest {
  parcel_id?: string;
  sg_code?: string;
  lat?: number;
  lng?: number;
  entity_id?: string;
  link?: boolean;
  parcel_ids?: string[];
  overwrite?: boolean;
}

/**
 * Owner of one anchor parcel (AfriGIS deeds) and every parcel that owner is
 * known to hold; with link + entity_id, links the matched parcels.
 * Errors come back as { success: false, error } instead of throwing.
 */
export function useMapperFindByEntity() {
  return useMutation({
    mutationFn: async (body: DeedsPortfolioRequest): Promise<DeedsPortfolioResult> => {
      try {
        return await cpFetch('/mapper/deeds/find-by-entity', {
          method: 'POST',
          body: JSON.stringify(body),
        });
      } catch (err) {
        const e = err as { status?: number; code?: string; message?: string };
        return {
          success: false,
          error: e?.code || 'deeds_error',
          message: e?.message || 'Deeds lookup unavailable',
        };
      }
    },
  });
}
